import { encodeFunctionData, keccak256, toHex } from 'viem';
import { MINT_GAS_LIMIT_MULTIPLIER, UINT32_MAX } from '../../../utils/constants.mjs';
import { InvalidUint32 } from '../../../utils/errors.mjs';
import { scaleAmount, curry } from '../../../utils/helpers.mjs';
import { SPLIT_MAIN_ABI, SPLIT_MAIN_ADDRESS } from '../abi/external/split-main.mjs';
import { SOUND_CREATOR_V2_ABI, SOUND_CREATOR_V2_ADDRESS } from '../abi/sound-creator-v2.mjs';
import { SOUND_EDITION_V2_ABI, SOUND_EDITION_V2_IMPLEMENTATION_ADDRESS } from '../abi/sound-edition-v2.mjs';
import { SOUND_METADATA_ADDRESS, SOUND_METADATA_ABI } from '../abi/sound-metadata.mjs';
import { SUPER_MINTER_V2_ADDRESS, SUPER_MINTER_V2_ABI } from '../abi/super-minter-v2.mjs';
import { MINTER_ROLE } from './helpers.mjs';

const EMPTY_MERKLE_ROOT = "0x0000000000000000000000000000000000000000000000000000000000000069";
function isValidUint32(value) {
  if (!Number.isSafeInteger(value) || value < 0 || value > UINT32_MAX)
    return false;
  return true;
}
function createTieredEditionArgs({
  owner,
  formattedSalt,
  precomputedEdition,
  editionConfig,
  tierConfigs,
  mintConfigs,
  createSplit
}) {
  const contractCalls = [];
  contractCalls.push({
    contractAddress: precomputedEdition,
    calldata: encodeFunctionData({
      abi: SOUND_EDITION_V2_ABI,
      functionName: "grantRoles",
      args: [SUPER_MINTER_V2_ADDRESS, MINTER_ROLE]
    })
  });
  for (const mintConfig of mintConfigs) {
    if (!isValidUint32(mintConfig.startTime)) {
      throw new InvalidUint32({
        field: "mintConfig.startTime",
        value: mintConfig.startTime
      });
    }
    if (!isValidUint32(mintConfig.endTime)) {
      throw new InvalidUint32({
        field: "mintConfig.endTime",
        value: mintConfig.endTime
      });
    }
    if (!isValidUint32(mintConfig.maxMintablePerAccount)) {
      throw new InvalidUint32({
        field: "mintConfig.maxMintablePerAccount",
        value: mintConfig.maxMintablePerAccount
      });
    }
    if (!isValidUint32(mintConfig.maxMintable)) {
      throw new InvalidUint32({
        field: "mintConfig.maxMintable",
        value: mintConfig.maxMintable
      });
    }
    contractCalls.push({
      contractAddress: SUPER_MINTER_V2_ADDRESS,
      calldata: encodeFunctionData({
        abi: SUPER_MINTER_V2_ABI,
        functionName: "createEditionMint",
        args: [
          {
            edition: precomputedEdition,
            price: mintConfig.price,
            startTime: Math.floor(mintConfig.startTime),
            endTime: Math.floor(mintConfig.endTime),
            maxMintablePerAccount: Math.floor(mintConfig.maxMintablePerAccount),
            maxMintable: Math.floor(mintConfig.maxMintable),
            affiliateFeeBPS: mintConfig.affiliateFeeBPS,
            affiliateMerkleRoot: mintConfig.affiliateMerkleRoot,
            tier: mintConfig.tier,
            platform: mintConfig.platform,
            // TODO: add better typesafety here
            mode: mintConfig.mode === "DEFAULT" ? 0 : mintConfig.mode === "VERIFY_MERKLE" ? 1 : 2,
            merkleRoot: mintConfig.mode === "VERIFY_MERKLE" ? mintConfig.merkleRoot : EMPTY_MERKLE_ROOT
          }
        ]
      })
    });
  }
  for (const tierConfig of tierConfigs) {
    contractCalls.push({
      contractAddress: SOUND_METADATA_ADDRESS,
      calldata: encodeFunctionData({
        abi: SOUND_METADATA_ABI,
        functionName: "setBaseURI",
        args: [precomputedEdition, tierConfig.tier, tierConfig.baseURI]
      })
    });
  }
  if (createSplit) {
    const { accountAllocations, controller, distributorFee } = createSplit;
    const sortedAccountAllocations = [...accountAllocations].sort(
      (a, b) => a.account.toLowerCase().localeCompare(b.account.toLowerCase())
    );
    const accounts = sortedAccountAllocations.map(({ account }) => account);
    const percentAllocations = sortedAccountAllocations.map(({ percentAllocation }) => percentAllocation);
    const splitCalldata = encodeFunctionData({
      abi: SPLIT_MAIN_ABI,
      functionName: "createSplit",
      args: [accounts, percentAllocations, distributorFee, controller]
    });
    contractCalls.push({
      contractAddress: precomputedEdition,
      calldata: encodeFunctionData({
        abi: SOUND_EDITION_V2_ABI,
        functionName: "createSplit",
        args: [SPLIT_MAIN_ADDRESS, splitCalldata]
      })
    });
  }
  const editionInitData = encodeFunctionData({
    abi: SOUND_EDITION_V2_ABI,
    functionName: "initialize",
    args: [
      {
        name: editionConfig.name,
        symbol: editionConfig.symbol,
        metadataModule: SOUND_METADATA_ADDRESS,
        baseURI: editionConfig.baseURI,
        contractURI: editionConfig.contractURI,
        fundingRecipient: editionConfig.fundingRecipient,
        royaltyBPS: editionConfig.royaltyBPS,
        isCreateTierFrozen: editionConfig.shouldFreezeTierCreation,
        isMetadataFrozen: editionConfig.shouldFreezeMetadata,
        tierCreations: tierConfigs.map((tierConfig) => {
          if (!isValidUint32(tierConfig.cutoffTime)) {
            throw new InvalidUint32({
              field: "tierConfig.cutoffTime",
              value: tierConfig.cutoffTime
            });
          }
          if (!isValidUint32(tierConfig.maxMintableLower)) {
            throw new InvalidUint32({
              field: "tierConfig.maxMintableLower",
              value: tierConfig.maxMintableLower
            });
          }
          if (!isValidUint32(tierConfig.maxMintableUpper)) {
            throw new InvalidUint32({
              field: "tierConfig.maxMintableUpper",
              value: tierConfig.maxMintableUpper
            });
          }
          return {
            tier: tierConfig.tier,
            cutoffTime: tierConfig.cutoffTime,
            isFrozen: tierConfig.isFrozen,
            maxMintableLower: tierConfig.maxMintableLower,
            maxMintableUpper: tierConfig.maxMintableUpper,
            mintRandomnessEnabled: tierConfig.mintRandomnessEnabled
          };
        })
      }
    ]
  });
  const addresses = contractCalls.map((d) => d.contractAddress);
  const calldata = contractCalls.map((d) => d.calldata);
  return {
    abi: SOUND_CREATOR_V2_ABI,
    functionName: "create",
    args: [
      {
        contracts: addresses,
        data: calldata,
        implementation: SOUND_EDITION_V2_IMPLEMENTATION_ADDRESS,
        initData: editionInitData,
        owner: typeof owner === "string" ? owner : owner.address,
        salt: formattedSalt,
        // doesn't matter outside of signature
        nonce: 0n
      }
    ]
  };
}
const FALLBACK_ESTIMATED_UPLOAD_GAS = 1000000n;
async function createEditionParameters(client, { gas, maxFeePerGas, maxPriorityFeePerGas, chain, ...args }) {
  const txnOverrides = {
    gas,
    maxFeePerGas,
    maxPriorityFeePerGas
  };
  const input = {
    ...createTieredEditionArgs(args),
    functionName: "create",
    address: SOUND_CREATOR_V2_ADDRESS,
    account: args.owner,
    ...txnOverrides,
    chain
  };
  let gasEstimate;
  try {
    gasEstimate = txnOverrides.gas = scaleAmount({
      amount: await client.estimateContractGas(input),
      multiplier: MINT_GAS_LIMIT_MULTIPLIER
    });
  } catch (err) {
    txnOverrides.gas = FALLBACK_ESTIMATED_UPLOAD_GAS;
    gasEstimate = null;
  }
  return {
    input: {
      ...input,
      ...txnOverrides
    },
    gasEstimate
  };
}
async function getExpectedEditionAddress(client, { deployer, salt: customSalt }) {
  const formattedSalt = keccak256(toHex(customSalt || Math.random() * 1e15));
  const [edition, exists] = await client.readContract({
    abi: SOUND_CREATOR_V2_ABI,
    address: SOUND_CREATOR_V2_ADDRESS,
    functionName: "soundEditionAddress",
    args: [SOUND_EDITION_V2_IMPLEMENTATION_ADDRESS, deployer, formattedSalt]
  });
  return {
    formattedSalt,
    edition,
    exists
  };
}
function editionV2PublicActionsCreate(client) {
  return {
    editionV2: {
      ...client.editionV2,
      createEditionParameters: curry(createEditionParameters)(client),
      getExpectedEditionAddress: curry(getExpectedEditionAddress)(client)
    }
  };
}

export { EMPTY_MERKLE_ROOT, FALLBACK_ESTIMATED_UPLOAD_GAS, createEditionParameters, createTieredEditionArgs, editionV2PublicActionsCreate, getExpectedEditionAddress };
